import type { LobbyGame } from '../api/getLobby';
import { isDailyDrawLobbyGame } from './lobbyDailyDrawPermission';
import { hasContestStartPassed } from './lobbyUpcomingByDate';

/** Normalized contest statuses shown in the lobby (excludes complete/draft and other terminal states). */
const PLAYABLE_STATUS_KEYS = new Set(['active', 'inplay', 'pending', 'open', 'scheduled']);

/** Non-terminal statuses eligible for dynamic section fill (lobby UI filters display). */
const DYNAMIC_FILL_STATUS_KEYS = PLAYABLE_STATUS_KEYS;

function normalizeContestStatus(status: string | null | undefined): string {
  if (!status) return '';
  return status
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/_/g, '');
}

/** Custom lobby links open an external URL (e.g. sportsbook pools) — not in-app pickems. */
export function isCustomLobbyGame(game: LobbyGame): boolean {
  return Boolean(game.external_url?.trim());
}

function openPickemUtcHasPassed(openPickemUtc: string | null | undefined): boolean {
  if (!openPickemUtc?.trim()) return false;
  const d = new Date(openPickemUtc);
  return !Number.isNaN(d.getTime()) && d <= new Date();
}

/**
 * Has the hard entry deadline passed? Unparseable values count as "not passed" — a bad date
 * must never be the reason a live contest disappears. Mirrors the backend helper of the same
 * name in `apps/backend/src/utils/lobbyPlayableStatus.ts`.
 */
export function hasEntryClosed(game: Pick<LobbyGame, 'entry_close_utc'>): boolean {
  const raw = game.entry_close_utc;
  if (!raw?.trim()) return false;
  const d = new Date(raw);
  return !Number.isNaN(d.getTime()) && d <= new Date();
}

function isIneligibleAfterContestStart(
  game: Pick<LobbyGame, 'status' | 'start_utc' | 'game_type'>
): boolean {
  // Daily Reveal / Survivor stay enterable after start (week / season formats).
  const gt = (game.game_type ?? '').trim().toLowerCase().replace(/\s+/g, '_').replace(/-+/g, '_');
  if (isDailyDrawLobbyGame(game) || gt === 'survivor') return false;

  const key = normalizeContestStatus(game.status);
  if (key === 'inplay') {
    return !game.start_utc?.trim() || hasContestStartPassed(game.start_utc);
  }
  if (key === 'active' && game.start_utc?.trim()) {
    return hasContestStartPassed(game.start_utc);
  }
  return false;
}

/**
 * Whether a lobby game should render in the UI.
 * Applies to flat layout, featured rows, section rows, and search.
 */
export function isDynamicFillEligibleStatus(status: string | null | undefined): boolean {
  const key = normalizeContestStatus(status);
  return Boolean(key && DYNAMIC_FILL_STATUS_KEYS.has(key));
}

/**
 * A contest pinned "Persistent" in the admin, for a viewer who already holds a LIVE entry. The
 * backend has already decided every part of this — the pin, the entry, and (for Survivor)
 * whether that entry is still alive — and will not serve such a tile to anyone else, so the
 * rules below, every one of which exists to hide a contest that can no longer be ENTERED, must
 * not take it back off the page.
 */
function isPersistentForThisViewer(game: LobbyGame): boolean {
  return Boolean(game.persist_for_entrants && game.has_entry);
}

export function isPlayableLobbyGame(game: LobbyGame): boolean {
  if (isPersistentForThisViewer(game)) return true;
  // The hard entry deadline, checked before anything else and for every game type. Only
  // survivor carries it today (week 1's week lock, past which the backend refuses every new
  // entry and seat activation), so this is inert for the rest; the lobby has no business
  // advertising ANY contest that can no longer be entered. Survivor's exemption from the
  // `start_utc` rule below stays: `start_utc` is its NEXT pick deadline, not its door.
  if (hasEntryClosed(game)) return false;
  // Custom games have no lifecycle status — the admin's "Start date & time" is the removal
  // signal. Once it passes the contest has begun and can no longer be entered, so the tile
  // comes down without manual intervention; with no start set, the game stays until
  // deactivated. End time is deliberately NOT a reason to keep a started game visible.
  if (isCustomLobbyGame(game)) return !hasContestStartPassed(game.start_utc);
  const key = normalizeContestStatus(game.status);
  if (key && PLAYABLE_STATUS_KEYS.has(key)) {
    if (isIneligibleAfterContestStart(game)) return false;
    return true;
  }
  if (!key && openPickemUtcHasPassed(game.open_pickem_utc)) return true;
  return false;
}

export function filterPlayableLobbyGames(games: LobbyGame[]): LobbyGame[] {
  return games.filter(isPlayableLobbyGame);
}
